import { Component, PropsWithChildren } from 'react'

const RELOAD_FLAG = 'cu:chunk-reload-attempted'

function isChunkError(err: unknown): boolean {
  const msg = (err as Error)?.message ?? String(err)
  return (
    msg.includes('text/html') ||
    msg.includes('MIME') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  )
}

interface State {
  hasError: boolean
}

export class ChunkErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(error: unknown): State | null {
    // Only handle chunk/MIME errors — let other errors propagate
    return isChunkError(error) ? { hasError: true } : null
  }

  componentDidUpdate(_: PropsWithChildren, prev: State) {
    if (this.state.hasError && !prev.hasError) {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1')
        window.location.reload()
      }
    }
  }

  componentDidMount() {
    // Clear reload flag 5 s after successful load so future updates can retry
    setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5000)
  }

  render() {
    if (this.state.hasError) {
      // Shown only when a reload was already attempted and still failed
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            textAlign: 'center',
            color: '#222',
            background: '#fff',
          }}
        >
          <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600 }}>Update required</h2>
          <p style={{ margin: '0 0 24px', maxWidth: 320, lineHeight: 1.5, color: '#555' }}>
            The app has updated. Tap Retry, or fully close and reopen the app.
          </p>
          <button
            style={{
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 500,
              border: 'none',
              borderRadius: 8,
              background: '#1677ff',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG)
              window.location.reload()
            }}
          >
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
